<template>
  <div class="story-card" :style="{ maxWidth: maxWidth }">
    <img :src="image" alt="Story Image" class="story-image" :style="{maxHeight: maxImageHeight}"/>
    <div class="story-content">
      <div
        v-if="showCard"
        class="d-flex justify-space-between align-center mb-2"
      >
        <span class="category-badge">{{ tags }}</span>
        <img :src="logo" alt="Company Logo" class="company-logo" />
      </div>
      <h3 class="story-title">{{ title }}</h3>
      <p class="story-description">{{ description }}</p>
      <button class="read-more" @click="goToStory">{{ buttonText }}</button>
    </div>
  </div>
</template>

<script setup>
import { defineProps, computed } from 'vue';
import { useRouter } from 'vue-router';

// Define props
const props = defineProps({
  image: {
    type: String,
    required: true,
  },
  tags: {
    type: String,
    required: false,
  },
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    required: true,
  },
  logo: {
    type: String,
  },
  route: {
    type: String,
    required: true,
  },
  showCard: {
    type: Boolean,
    default: false,
  },
  buttonText: {
    type: String,
    default: 'Read more',
  },
  maxWidth: {
    type: [String, Number],
    default: '350px',
  },
  maxImageHeight: {
    type: [String, Number],
    default: '300px',
  },
  aspectRatio: {
    type: [String, Number],
    default: 1.777,
  },
});

// Computed property to handle maxWidth formatting
const maxWidth = computed(() => {
  if (typeof props.maxWidth === 'number') {
    return `${props.maxWidth}px`;
  }
  return props.maxWidth;
});

const maxImageHeight = computed(() => {
  if (typeof props.maxImageHeight === 'number') {
    return `${props.maxImageHeight}px`;
  }
  return props.maxImageHeight;
});

// Computed property to handle aspectRatio formatting
const imageAspectRatio = computed(() => {
  return props.aspectRatio;
});

// Router setup
const router = useRouter();

// Navigation function
const goToStory = () => {
  // Navigate to the story page
  router.push(props.route).then(() => {
    // Scroll to the top of the page after navigation
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }, 100); // Small delay to ensure the page has loaded
  });
};
</script>

<style scoped>
.story-card {
  border-radius: 12px;
  overflow: hidden;
  background: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  text-align: left;
  margin-bottom: 20px;
  min-height: 425px;
  display: flex;
  flex-direction: column;
}

.story-image {
  width: 100%;
  padding-bottom: 0;
  object-fit: cover;
  aspect-ratio: v-bind(imageAspectRatio);
  display: block;
}

.story-content {
  padding: 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.category-badge {
  display: inline-block;
  color: #808080;
  padding: 6px 12px;
  border-radius: 20px;
  border: 1px solid #808080;
  font-size: 12px;
  font-weight: 500;
  width: fit-content;
}

.company-logo {
  height: 40px;
}

.story-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 10px 0;
  color: #333;
}

.story-description {
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
  line-height: 1.5;
  flex-grow: 1;
}

.read-more {
  background: #007ab2;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  width: fit-content;
}

.read-more:hover {
  background: #00aeff;
}

@media only screen and (max-width: 960px) {
  .story-card {
    max-height: none;
    margin-bottom: 0;
  }
}
</style>